import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface TierBreakdown {
  planCode: string;
  displayName: string;
  displayTier: string;
  activeCount: number;
}

interface PlanMixDonutProps {
  tierBreakdown: TierBreakdown[];
  totalActive: number;
}

const TIER_COLORS: Record<string, string> = {
  standard: '#0ea5e9',
  premium: '#7c3aed',
  legacy_creator: '#d97706',
  vault_standard: '#146152',
  vault_premium: '#1A5F7A',
  trial: '#94A3B8',
  free: '#D1D5DB',
  other: '#6B7280',
};

const PlanMixDonut: React.FC<PlanMixDonutProps> = ({ tierBreakdown, totalActive }) => {
  // Aggregate by display tier
  const tierMap = new Map<string, { name: string; count: number; color: string }>();
  tierBreakdown.forEach((t) => {
    const tier = t.displayTier || 'other';
    const existing = tierMap.get(tier);
    if (existing) {
      existing.count += t.activeCount;
    } else {
      tierMap.set(tier, {
        name: t.displayName || tier,
        count: t.activeCount,
        color: TIER_COLORS[tier] || '#6B7280',
      });
    }
  });

  const chartData = Array.from(tierMap.values()).filter((d) => d.count > 0);

  // If no data, show a single gray segment
  if (chartData.length === 0) {
    chartData.push({ name: 'No active subs', count: 1, color: '#E5E7EB' });
  }

  return (
    <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', height: '100%' }}>
      <Typography
        sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '13px',
          fontWeight: 700,
          color: '#111827',
          mb: 2,
        }}
      >
        Plan Mix
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <div style={{ width: '100%', height: 200, minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              dataKey="count"
              nameKey="name"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '12px',
                borderRadius: '8px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        </div>
        {/* Center text */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '24px',
              fontWeight: 800,
              color: '#111827',
              lineHeight: 1,
            }}
          >
            {totalActive}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '9px',
              fontWeight: 600,
              color: '#6B7280',
              textTransform: 'uppercase',
            }}
          >
            Active
          </Typography>
        </Box>
      </Box>
      {/* Legend */}
      <Box sx={{ mt: 1 }}>
        {Array.from(tierMap.values()).map((item) => (
          <Box
            key={item.name}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                {item.name}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, color: '#111827' }}>
              {item.count}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default PlanMixDonut;
